import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import API from './api';

/* ---------- Toast UI (bez biblioteka) ---------- */
function ToastContainer({ toasts, onClose }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map(t => {
        const color =
          t.type === 'success' ? 'bg-green-600' :
          t.type === 'error'   ? 'bg-red-600'   :
          t.type === 'warn'    ? 'bg-yellow-600':
                                 'bg-blue-600';
        const emoji =
          t.type === 'success' ? '✅' :
          t.type === 'error'   ? '⛔' :
          t.type === 'warn'    ? '⚠️' :
                                 'ℹ️';
        return (
          <div
            key={t.id}
            className={`${color} text-white rounded-lg shadow-lg px-4 py-3 min-w-[280px] max-w-[420px] animate-[fadein_120ms_ease-out]`}
          >
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none">{emoji}</span>
              <div className="flex-1 text-sm whitespace-pre-wrap">{t.text}</div>
              <button
                className="opacity-80 hover:opacity-100"
                onClick={() => onClose(t.id)}
                aria-label="Close"
              >✖</button>
            </div>
          </div>
        );
      })}
      {/* mala keyframes animacija (inline) */}
      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

/* ---------- Time helpers (robustno HH:mm) ---------- */
function normalizeTime(val) {
  if (!val) return '';
  const s = String(val).trim();
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const part = ampm[3].toUpperCase();
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  }
  const m2 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m2) {
    const h = Math.min(23, Math.max(0, parseInt(m2[1],10)));
    const mi = Math.min(59, Math.max(0, parseInt(m2[2],10)));
    return `${String(h).padStart(2,'0')}:${String(mi).padStart(2,'0')}`;
  }
  return '';
}
function toMinutes(t) {
  const n = normalizeTime(t);
  if (!n) return 0;
  const [h, m] = n.split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}
function normalizeMapByDate(map, clampFn) {
  const out = {};
  Object.entries(map || {}).forEach(([date, list]) => {
    out[date] = (list || [])
      .map(s => {
        let from = normalizeTime(s.from_time ?? s.from);
        let to   = normalizeTime(s.to_time ?? s.to);
        if (!from || !to) return null;
        if (clampFn) {
          from = clampFn(from);
          to   = clampFn(to);
        }
        return {
          from_time: from,
          to_time: to,
          location_choice: s.location_choice || 'primary'
        };
      })
      .filter(Boolean);
  });
  return out;
}

/* ---------- Component ---------- */
function MyStudentsAvailability({ mode = 'teacher' }) {
  const isAdmin = mode === 'admin';

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentInfo, setStudentInfo] = useState(null);

  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  const [availability, setAvailability] = useState({});
  const [exceptions, setExceptions] = useState({});
  const [calendarHighlights, setCalendarHighlights] = useState({});
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

  // Toast state
  const [toasts, setToasts] = useState([]);
  const pushToast = (type, text, ttlMs = 3800) => {
    const id = Math.random().toString(36).slice(2);
    setToasts(prev => [...prev, { id, type, text }]);
    // auto close
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, ttlMs);
  };
  const closeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyWeeksForward, setCopyWeeksForward] = useState(1);
  const [copyMode, setCopyMode] = useState('append'); // or 'overwrite'

  // Workday iz settings-a (fallback 08–18)
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });
  const dayStartM = toMinutes(workday.start || '08:00');
  const dayEndM   = toMinutes(workday.end || '18:00');

  // hasAlt?
  const hasAlt = !!(studentInfo?.alt_address_street || studentInfo?.alt_latitude || studentInfo?.alt_longitude);

  useEffect(() => {
    API.get('/workday-settings')
      .then(res => {
        const start = normalizeTime(res.data?.start || '08:00');
        const end   = normalizeTime(res.data?.end || '18:00');
        setWorkday({ start, end });
      })
      .catch(() => {});
  }, []);

  // 1) Studenti po pravima
  useEffect(() => {
    API.get('/assigned').then(res => setStudents(res.data || []));
  }, []);

  // 2) Nedelje
  useEffect(() => {
    API.get('/weeks').then(res => setWeekOptions(res.data || []));
  }, []);

  // 3) Highlights
  useEffect(() => {
    if (!selectedStudent) { setCalendarHighlights({}); return; }
    API.get(`/student-availability/highlight/${selectedStudent}`)
      .then(res => setCalendarHighlights(res.data || {}))
      .catch(() => setCalendarHighlights({}));
  }, [selectedStudent]);

  // 3.1) Učitaj info o studentu (radi hasAlt)
  useEffect(() => {
    if (!selectedStudent) { setStudentInfo(null); return; }
    API.get(`/students/${selectedStudent}`)
      .then(res => setStudentInfo(res.data || null))
      .catch(() => setStudentInfo(null));
  }, [selectedStudent]);

  // 4) Availability + week dates
  useEffect(() => {
    if (!selectedStudent || !selectedWeekStart) return;
    const clamp = (hhmm) => {
      const m = Math.min(dayEndM, Math.max(dayStartM, toMinutes(hhmm)));
      return minutesToHHMM(m);
    };
    API.get(`/student-availability/week/${selectedStudent}/${selectedWeekStart}`)
      .then(res => {
        setAvailability(normalizeMapByDate(res.data?.availability || {}, clamp));
        setExceptions(normalizeMapByDate(res.data?.exceptions || {}, clamp));
      })
      .catch(() => { setAvailability({}); setExceptions({}); });

    API.get(`/week-dates/${selectedWeekStart}`)
      .then(res => setWeekDates(res.data || []));
  }, [selectedStudent, selectedWeekStart, dayStartM, dayEndM]);

  // Kalendar tile class
  const getTileClassName = ({ date }) => {
    const d = date.toISOString().slice(0, 10);
    if (calendarHighlights[d] === 'exception') return 'bg-yellow-200';
    if (calendarHighlights[d] === 'default')   return 'bg-green-200';
    return 'bg-red-200';
  };

  // Klamp u radni dan
  const clampToWorkday = (hhmm) => {
    const m = Math.min(dayEndM, Math.max(dayStartM, toMinutes(hhmm)));
    return minutesToHHMM(m);
  };

  // Poboljšan showMessage -> i toast + inline
  const showMessage = (type, text) => {
    setStatusMessage({ type, text });
    pushToast(type === 'error' ? 'error' : (type === 'warn' ? 'warn' : 'success'), text);
  };

  // Add slot
  const handleAddSlot = (target, setter, date, isException = false) => {
    if (isException && (!availability[date] || availability[date].length === 0)) {
      return showMessage('error', `Cannot add exception for ${date} — no availability`);
    }
    const existing = target[date] || [];
    if (existing.length > 0) {
      return showMessage('warn', `Slots already exist for ${date}. You can only modify or delete them.`);
    }

    setter(prev => {
      const out = { ...prev };
      if (isException) {
        const base = (availability[date] || [])[0] || { from_time: workday.start, to_time: workday.end };
        const exFromM = Math.max(dayStartM, toMinutes(base.from_time));
        const exToM   = Math.min(dayEndM,   toMinutes(base.to_time));
        const to60    = Math.min(exToM, exFromM + 60);
        out[date] = [{ from_time: minutesToHHMM(exFromM), to_time: minutesToHHMM(to60) }];
      } else {
        out[date] = [{
          from_time: workday.start,
          to_time: workday.end,
          location_choice: 'primary'
        }];
      }
      return out;
    });
  };

  // Delete slot
  const handleDeleteSlot = (setter, date, index) => {
    setter(prev => {
      const out = { ...prev };
      const rows = (out[date] || []).slice();
      rows.splice(index, 1);
      if (rows.length) out[date] = rows; else delete out[date];
      return out;
    });
  };

  // Change – raw value
  const handleTimeChange = (setter, date, index, field, value) => {
    setter(prev => {
      const out = { ...prev };
      const rows = (out[date] || []).slice();
      const row  = { ...(rows[index] || {}) };
      row[field] = value ?? '';
      rows[index] = row;
      out[date] = rows;
      return out;
    });
  };

  // Blur – normalize + clamp + ensure from < to
  const handleTimeBlur = (setter, date, index) => {
    setter(prev => {
      const out = { ...prev };
      const rows = (out[date] || []).slice();
      const row  = { ...(rows[index] || {}) };

      let from = row.from_time ? normalizeTime(row.from_time) : workday.start;
      let to   = row.to_time   ? normalizeTime(row.to_time)   : minutesToHHMM(Math.min(dayEndM, toMinutes(from) + 30));
      from = clampToWorkday(from);
      to   = clampToWorkday(to);

      const f = toMinutes(from);
      let t   = toMinutes(to);
      if (t <= f) {
        t = Math.min(dayEndM, f + 30);
        to = minutesToHHMM(t);
      }

      row.from_time = from;
      row.to_time   = to;
      rows[index]   = row;
      out[date]     = rows;
      return out;
    });
  };

  // Save
  const handleSaveAll = () => {
    if (!selectedStudent || !selectedWeekStart) return showMessage('error', 'Select student and week');

    const sanitize = (map) => {
      const out = {};
      Object.entries(map || {}).forEach(([d, list]) => {
        out[d] = (list || []).map(s => {
          let from = normalizeTime(s.from_time || workday.start);
          let to   = normalizeTime(s.to_time || minutesToHHMM(toMinutes(from) + 30));
          from = clampToWorkday(from);
          to   = clampToWorkday(to);
          const f = toMinutes(from), t0 = toMinutes(to);
          if (t0 <= f) to = minutesToHHMM(Math.min(dayEndM, f + 30));

          const lc = (s.location_choice === 'alt' && hasAlt) ? 'alt' : 'primary';
          return { from_time: from, to_time: to, location_choice: lc };
        });
      });
      return out;
    };

    const safeAvailability = sanitize(availability);
    const safeExceptions   = sanitize(exceptions);

    setAvailability(safeAvailability);
    setExceptions(safeExceptions);

    API.post(`/student-availability/week/${selectedStudent}/${selectedWeekStart}`, {
      availability: safeAvailability,
      exceptions: safeExceptions
    })
    .then(() => {
      showMessage('success', 'Saved: availability & exceptions.');
      // refresh highlights da se vidi na kalendaru
      API.get(`/student-availability/highlight/${selectedStudent}`)
        .then(res => setCalendarHighlights(res.data || {}));
    })
    .catch(() => showMessage('error', 'Save failed.'));
  };

  // Ako je izabran student a nedelja nije – selektuj prvu
  useEffect(() => {
    if (selectedStudent && weekOptions.length && !selectedWeekStart) {
      setSelectedWeekStart(weekOptions[0].value);
    }
  }, [selectedStudent, weekOptions, selectedWeekStart]);

  // Copy week actions
  const copyToNextWeek = () => {
    if (!selectedStudent || !selectedWeekStart) return showMessage('error', 'Select student and week');
    API.post(`/student-availability/copy-week/${selectedStudent}`, {
      from_week_start: selectedWeekStart,
      weeks_forward: 1,
      mode: 'append'
    }).then(res => {
      // novi BE shape: availability/exceptions inserted/skipped
      const t = res?.data?.targets?.[0];
      const aIns = t?.availability?.inserted ?? 0;
      const eIns = t?.exceptions?.inserted ?? 0;
      showMessage('success', `Copied to next week → availability:+${aIns}, exceptions:+${eIns}`);
      // refresh highlights (sledeća nedelja nije prikazana ali ok da osvežimo trenutnu)
      API.get(`/student-availability/highlight/${selectedStudent}`)
        .then(r => setCalendarHighlights(r.data || {}));
    }).catch(() => showMessage('error', 'Copy failed'));
  };

  const openCopyModal = () => setCopyModalOpen(true);
  const closeCopyModal = () => setCopyModalOpen(false);

  const confirmCopy = () => {
    if (!selectedStudent || !selectedWeekStart) return showMessage('error', 'Select student and week');
    const wf = Number(copyWeeksForward) || 1;
    API.post(`/student-availability/copy-week/${selectedStudent}`, {
      from_week_start: selectedWeekStart,
      weeks_forward: wf,
      mode: copyMode
    }).then(res => {
      const targets = res?.data?.targets || [];
      const aTotal = targets.reduce((s, t) => s + (t?.availability?.inserted || 0), 0);
      const eTotal = targets.reduce((s, t) => s + (t?.exceptions?.inserted || 0), 0);
      showMessage('success', `Copied ${targets.length} week(s) (${copyMode}) → availability:+${aTotal}, exceptions:+${eTotal}`);
      closeCopyModal();
      // refresh highlights
      API.get(`/student-availability/highlight/${selectedStudent}`)
        .then(r => setCalendarHighlights(r.data || {}));
    }).catch(() => showMessage('error', 'Copy failed'));
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <h2 className="text-lg font-bold mb-1">
        Student Weekly Availability{isAdmin ? ' (Admin)' : ''}
      </h2>
      <div className="text-sm text-gray-600 mb-6">
        Workday: <b>{workday.start}–{workday.end}</b>
      </div>

      {/* Student dropdown */}
      <label className="block mb-2">Select student:</label>
      <select
        className="border px-2 py-1 mb-6"
        value={selectedStudent}
        onChange={e => { setSelectedStudent(e.target.value); setSelectedWeekStart(''); }}
      >
        <option value="">-- Choose student --</option>
        {students
          .slice()
          .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || ''))
          .map(s => (
            <option key={s.id} value={s.id}>
              {(s.last_name || '') + ', ' + (s.first_name || '')}
            </option>
          ))}
      </select>

      {/* Nedelja */}
      {selectedStudent && (
        <>
          <label className="block mb-2">Select a week:</label>
          <select
            className="border px-2 py-1 mb-6"
            value={selectedWeekStart}
            onChange={e => setSelectedWeekStart(e.target.value)}
          >
            <option value="">-- Choose week --</option>
            {weekOptions.map((week, idx) => (
              <option key={idx} value={week.value}>{week.label}</option>
            ))}
          </select>

          {/* Copy controls */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={copyToNextWeek}
              disabled={!selectedWeekStart}
              className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50"
            >
              Copy to next week
            </button>
            <button
              onClick={openCopyModal}
              disabled={!selectedWeekStart}
              className="bg-blue-500 text-white px-3 py-1 rounded disabled:opacity-50"
            >
              Copy to N weeks…
            </button>
          </div>
        </>
      )}

      {/* Slotovi */}
      {weekDates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {weekDates.map(({ dayName, date }) => {
            const hasAvail = availability[date]?.length > 0;
            const hasEx    = exceptions[date]?.length > 0;
            const isWeekend = ['Saturday', 'Sunday'].includes(dayName);
            const bgColor = hasEx ? 'bg-yellow-100'
                         : hasAvail ? 'bg-green-100'
                         : isWeekend ? ''
                         : 'bg-red-100';
            return (
              <div key={date} className={`border p-3 rounded shadow ${bgColor}`}>
                <h4 className="font-bold mb-2">{dayName} – {date}</h4>

                {(availability[date] || []).map((slot, index) => (
                  <div key={`a-${index}`} className="flex flex-col gap-2 mb-2">
                    <div className="flex gap-2 items-center">
                      <input
                        type="time" lang="en-GB" step="60"
                        value={slot.from_time || ''}
                        onChange={e => handleTimeChange(setAvailability, date, index, 'from_time', e.target.value)}
                        onBlur={() => handleTimeBlur(setAvailability, date, index)}
                        className="border px-2 py-1"
                      />
                      <span>to</span>
                      <input
                        type="time" lang="en-GB" step="60"
                        value={slot.to_time || ''}
                        onChange={e => handleTimeChange(setAvailability, date, index, 'to_time', e.target.value)}
                        onBlur={() => handleTimeBlur(setAvailability, date, index)}
                        className="border px-2 py-1"
                      />
                      <button
                        onClick={() => handleDeleteSlot(setAvailability, date, index)}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >🗑</button>
                    </div>

                    {hasAlt && (
                      <div className="flex items-center gap-3 text-sm">
                        <span className="opacity-70">Location:</span>
                        <label className={`px-2 py-0.5 rounded border cursor-pointer
                          ${slot.location_choice === 'primary' ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}>
                          <input
                            type="radio"
                            className="mr-1"
                            checked={slot.location_choice === 'primary'}
                            onChange={() => handleTimeChange(setAvailability, date, index, 'location_choice', 'primary')}
                          />
                          Primary
                        </label>
                        <label className={`px-2 py-0.5 rounded border cursor-pointer
                          ${slot.location_choice === 'alt' ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}>
                          <input
                            type="radio"
                            className="mr-1"
                            checked={slot.location_choice === 'alt'}
                            onChange={() => handleTimeChange(setAvailability, date, index, 'location_choice', 'alt')}
                            disabled={!hasAlt}
                          />
                          Alt
                        </label>
                      </div>
                    )}
                  </div>
                ))}

                {(exceptions[date] || []).map((slot, index) => (
                  <div key={`e-${index}`} className="flex gap-2 mb-1 items-center text-yellow-700">
                    <input
                      type="time" lang="en-GB" step="60"
                      value={slot.from_time || ''}
                      onChange={e => handleTimeChange(setExceptions, date, index, 'from_time', e.target.value)}
                      onBlur={() => handleTimeBlur(setExceptions, date, index)}
                      className="border px-2 py-1"
                    />
                    <span>to</span>
                    <input
                      type="time" lang="en-GB" step="60"
                      value={slot.to_time || ''}
                      onChange={e => handleTimeChange(setExceptions, date, index, 'to_time', e.target.value)}
                      onBlur={() => handleTimeBlur(setExceptions, date, index)}
                      className="border px-2 py-1"
                    />
                    <button
                      onClick={() => handleDeleteSlot(setExceptions, date, index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >🗑</button>
                  </div>
                ))}

                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => handleAddSlot(availability, setAvailability, date, false)}
                    className="bg-green-500 text-white px-2 py-1 rounded text-sm"
                  >+ Availability</button>
                  <button
                    onClick={() => handleAddSlot(exceptions, setExceptions, date, true)}
                    className="bg-yellow-500 text-white px-2 py-1 rounded text-sm"
                  >+ Exception</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Save */}
      {selectedWeekStart && (
        <div className="mt-8">
          <button onClick={handleSaveAll} className="bg-green-600 text-white px-4 py-2 rounded">
            Save All
          </button>
          {statusMessage.text && (
            <div className={`mt-2 text-sm font-semibold ${statusMessage.type === 'success' ? 'text-green-600' : statusMessage.type === 'warn' ? 'text-yellow-600' : 'text-red-600'}`}>
              {statusMessage.text}
            </div>
          )}
        </div>
      )}

      {/* Calendar preview */}
      <div className="mt-10">
        <h3 className="text-xl font-bold mb-2">Calendar Preview</h3>
        <Calendar tileClassName={getTileClassName} />
        <div className="mt-2 text-sm flex gap-4">
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-200 inline-block" /> Availability</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-200 inline-block" /> Exceptions</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-200 inline-block" /> None</span>
        </div>
      </div>

      {/* Copy modal */}
      {copyModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-4 w-full max-w-md shadow-xl">
            <h4 className="text-lg font-bold mb-4">Copy this week</h4>
            <div className="mb-3">
              <label className="block text-sm mb-1">Number of weeks forward:</label>
              <input
                type="number"
                min={1}
                max={52}
                value={copyWeeksForward}
                onChange={e => setCopyWeeksForward(e.target.value)}
                className="border px-2 py-1 w-24"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">Mode:</label>
              <select
                className="border px-2 py-1"
                value={copyMode}
                onChange={e => setCopyMode(e.target.value)}
              >
                <option value="append">Append (keep existing, no duplicates)</option>
                <option value="overwrite">Overwrite (replace target weeks)</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={closeCopyModal} className="px-3 py-1 rounded border">Cancel</button>
              <button onClick={confirmCopy} className="px-3 py-1 rounded bg-blue-600 text-white">Copy</button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <ToastContainer toasts={toasts} onClose={closeToast} />
    </div>
  );
}

export default MyStudentsAvailability;






